import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AllergyInput from '@/components/ui/allergy-input';
import AllergyBadge from '@/components/ui/allergy-badge';
import { patientsApi } from '../../../services/patientsService';
import { useSelector } from 'react-redux';
import { selectCurrentClinicId } from '../../../store/slices/authSlice';
import type { Patient, UpdatePatientRequest, Medication } from '../../../types/api';
import type { Api } from '../../../types/generated';
import { hasChanges, getChangedFields } from '../../../utils/patientUtils';
import { 
  validateForm, 
  formatPhoneNumber, 
  formatZipCode, 
  formatState,
  clearValidationError,
  hasValidationErrors,
  type ValidationErrors 
} from '../../../utils/patientValidation';

interface EditPatientModalProps {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (patientId: string, data: Partial<UpdatePatientRequest>) => Promise<void>;
  loading?: boolean;
}

const EditPatientModal: React.FC<EditPatientModalProps> = ({
  patient,
  isOpen,
  onClose,
  onSave,
  loading = false
}) => {
  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    phone: string;
    email: string;
    dob: string;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    address_lines: string;
    address_city: string;
    address_state: string;
    address_postal_code: string;
  }>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    dob: '',
    gender: 'male',
    address_lines: '',
    address_city: '',
    address_state: '',
    address_postal_code: ''
  });

  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Allergies state - using new API types
  const [selectedAllergies, setSelectedAllergies] = useState<Medication[]>([]);
  const [currentAllergies, setCurrentAllergies] = useState<Api.ClinicPatientAllergySearch200Response.Item[]>([]);
  const [allergiesLoading, setAllergiesLoading] = useState(false);
  const [allergiesError, setAllergiesError] = useState<string | null>(null);
  
  const currentClinicId = useSelector(selectCurrentClinicId);
  const { toast } = useToast();

  // Initialize form data when patient changes
  useEffect(() => {
    if (patient && isOpen) {
      const initialFormData = {
        first_name: patient.first_name || '',
        last_name: patient.last_name || '',
        phone: patient.phone ? formatPhoneNumber(patient.phone) : '',
        email: patient.email || '',
        dob: patient.date_of_birth || '',
        gender: patient.gender || 'male' as 'male' | 'female' | 'other' | 'prefer_not_to_say',
        address_lines: patient.address?.street?.join(', ') || '',
        address_city: patient.address?.city || '',
        address_state: patient.address?.state ? formatState(patient.address.state) : '',
        address_postal_code: patient.address?.postal_code ? formatZipCode(patient.address.postal_code) : ''
      };
      setFormData(initialFormData);
      setHasFormChanges(false);
      setErrors({}); // Clear validation errors when opening
      
      // Load allergies separately via API
      if (currentClinicId) {
        loadPatientAllergies();
      }
    } else if (!isOpen) {
      // Clear allergies when modal closes to prevent stale state
      setSelectedAllergies([]);
      setCurrentAllergies([]);
      setAllergiesError(null);
      setErrors({}); // Clear validation errors when closing
    }
  }, [patient, currentClinicId, isOpen]);

  // Load allergies from API using HATEOAS
  const loadPatientAllergies = async () => {
    console.log(`[EditPatientModal] loadPatientAllergies called for patient:`, patient?.id);
    if (!patient) {
      console.log(`[EditPatientModal] Early return - no patient`);
      return;
    }
    
    // Check if allergies is a HATEOAS link or populated array
    if (typeof patient.allergies === 'object' && 'href' in patient.allergies) {
      console.log(`[EditPatientModal] Loading allergies from HATEOAS href:`, patient.allergies.href);
      setAllergiesLoading(true);
      setAllergiesError(null);
      
      try {
        const patientAllergies = await patientsApi.getPatientAllergies(patient.allergies.href);
        console.log(`[EditPatientModal] Got allergies:`, patientAllergies);
        setCurrentAllergies(patientAllergies);
        
        // Convert to Medication format for compatibility with existing UI
        const medicationAllergies: Medication[] = patientAllergies.map(allergy => ({
          id: allergy.id || '',
          name: allergy.name || 'Unknown',
          category: '' // Not available in allergy response
        }));
        console.log(`[EditPatientModal] Converted medications:`, medicationAllergies);
        setSelectedAllergies(medicationAllergies);
      } catch (error) {
        console.error('[EditPatientModal] Failed to load patient allergies:', error);
        setAllergiesError('Failed to load allergies');
        setSelectedAllergies([]);
      } finally {
        setAllergiesLoading(false);
        console.log(`[EditPatientModal] Finished allergy load for ${patient.id}`);
      }
    } else if (Array.isArray(patient.allergies)) {
      // Allergies already populated as array
      console.log(`[EditPatientModal] Allergies already populated:`, patient.allergies);
      setSelectedAllergies(patient.allergies);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!patient) return;

    setIsSubmitting(true);

    // Validate form data
    const formDataForValidation = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      email: formData.email,
      date_of_birth: formData.dob,
      gender: formData.gender,
      address_street: formData.address_lines,
      address_city: formData.address_city,
      address_state: formData.address_state,
      address_postalCode: formData.address_postal_code
    };

    const validationErrors = validateForm(formDataForValidation, true); // true = isEdit mode
    setErrors(validationErrors);

    // If there are validation errors, don't submit
    if (hasValidationErrors(validationErrors)) {
      setIsSubmitting(false);
      return;
    }

    const updateData: Partial<UpdatePatientRequest> = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone: formData.phone.trim() || null,
      email: formData.email.trim() || null,
      date_of_birth: formData.dob,
      gender: formData.gender,
      address: {
        street: [formData.address_lines.trim()],
        city: formData.address_city.trim(),
        state: formData.address_state.trim(),
        postal_code: formData.address_postal_code.trim()
      }
    };
    
    console.log('Submitting patient update with data:', updateData);

    try {
      await onSave(patient.id, updateData);
      // Reset validation state on success
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to update patient:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Clear validation error for this field when user starts typing
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => clearValidationError(prev, field as keyof ValidationErrors));
    }
    
    // Update hasChanges state (including allergies)
    if (patient) {
      setHasFormChanges(hasChanges(patient, newFormData, selectedAllergies));
    }
  };

  // Specialized handler for phone number with real-time formatting
  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    handleInputChange('phone', formatted);
  };

  // Specialized handler for state with auto-uppercase
  const handleStateChange = (value: string) => {
    const formatted = formatState(value);
    handleInputChange('address_state', formatted);
  };

  const handleZipCodeChange = (value: string) => {
    const formatted = formatZipCode(value);
    handleInputChange('address_postal_code', formatted);
  };

  const handleAddAllergy = async (medication: Medication) => {
    if (!selectedAllergies.find(allergy => allergy.id === medication.id) && patient && 
        typeof patient.allergies === 'object' && 'href' in patient.allergies) {
      // Optimistic update - update UI immediately
      const newAllergies = [...selectedAllergies, medication];
      setSelectedAllergies(newAllergies);
      setHasFormChanges(hasChanges(patient, formData, newAllergies));
      
      // Show immediate feedback
      toast({
        description: `Adding ${medication.name}...`,
        duration: 1000,
      });
      
      try {
        // API call in background using HATEOAS href
        await patientsApi.addPatientAllergies(patient.allergies.href, [medication.id]);
        // Success - UI already updated
        toast({
          description: `✅ ${medication.name} added to allergies`,
          duration: 2000,
        });
      } catch (error) {
        console.error('Failed to add allergy:', error);
        // Rollback on failure
        setSelectedAllergies(selectedAllergies);
        setHasFormChanges(hasChanges(patient, formData, selectedAllergies));
        toast({
          variant: "destructive",
          description: `Failed to add ${medication.name}. Please try again.`,
          duration: 3000,
        });
      }
    }
  };

  const handleRemoveAllergy = async (medicationId: string) => {
    if (patient && typeof patient.allergies === 'object' && 'href' in patient.allergies) {
      // Store original state for rollback
      const originalAllergies = selectedAllergies;
      const removedMedication = selectedAllergies.find(a => a.id === medicationId);
      
      // Optimistic update - update UI immediately
      const newAllergies = selectedAllergies.filter(allergy => allergy.id !== medicationId);
      setSelectedAllergies(newAllergies);
      setHasFormChanges(hasChanges(patient, formData, newAllergies));
      
      try {
        // API call in background using HATEOAS href
        await patientsApi.removePatientAllergies(patient.allergies.href, [medicationId]);
        // Success - UI already updated
        toast({
          description: `✅ ${removedMedication?.name || 'Allergy'} removed`,
          duration: 2000,
        });
      } catch (error) {
        console.error('Failed to remove allergy:', error);
        // Rollback on failure
        setSelectedAllergies(originalAllergies);
        setHasFormChanges(hasChanges(patient, formData, originalAllergies));
        toast({
          variant: "destructive",
          description: `Failed to remove ${removedMedication?.name || 'allergy'}. Please try again.`,
          duration: 3000,
        });
      }
    }
  };

  if (!patient) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-syne font-medium text-vitl-navy flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
              <Edit className="w-6 h-6" />
            </div>
            <div>
              <div>Edit Patient: {patient.first_name} {patient.last_name}</div>
              <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                Update patient information and medical records
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">
                  First Name *
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('first_name', e.target.value)}
                  className={`mt-1 ${errors.first_name ? 'border-red-500' : ''}`}
                  placeholder="First name"
                  required
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('last_name', e.target.value)}
                  className={`mt-1 ${errors.last_name ? 'border-red-500' : ''}`}
                  placeholder="Last name"
                  required
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePhoneChange(e.target.value)}
                  className={`mt-1 ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  className={`mt-1 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="patient@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dob" className="text-sm font-medium text-gray-700">
                  Date of Birth *
                </Label>
                <Input
                  id="dob"
                  type="date"
                  value={formData.dob}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('dob', e.target.value)}
                  className={`mt-1 ${errors.date_of_birth ? 'border-red-500' : ''}`}
                  required
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>
                )}
              </div>
              <div>
                <Label htmlFor="gender" className="text-sm font-medium text-gray-700">
                  Gender *
                </Label>
                <select 
                  id="gender"
                  value={formData.gender}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('gender', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vitl-green"
                  required
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Address Information</h3>
            
            <div>
              <Label htmlFor="address_lines" className="text-sm font-medium text-gray-700">
                Street Address
              </Label>
              <Input
                id="address_lines"
                value={formData.address_lines}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('address_lines', e.target.value)}
                className={`mt-1 ${errors.address_street ? 'border-red-500' : ''}`}
                placeholder="123 Main Street, Apt 4B"
              />
              {errors.address_street && (
                <p className="mt-1 text-sm text-red-600">{errors.address_street}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="address_city" className="text-sm font-medium text-gray-700">
                  City
                </Label>
                <Input
                  id="address_city"
                  value={formData.address_city}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('address_city', e.target.value)}
                  className={`mt-1 ${errors.address_city ? 'border-red-500' : ''}`}
                  placeholder="New York"
                />
                {errors.address_city && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_city}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address_state" className="text-sm font-medium text-gray-700">
                  State
                </Label>
                <Input
                  id="address_state"
                  value={formData.address_state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleStateChange(e.target.value)}
                  className={`mt-1 ${errors.address_state ? 'border-red-500' : ''}`}
                  placeholder="NY"
                  maxLength={2}
                />
                {errors.address_state && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_state}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address_postal_code" className="text-sm font-medium text-gray-700">
                  Zip Code
                </Label>
                <Input
                  id="address_postal_code"
                  value={formData.address_postal_code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleZipCodeChange(e.target.value)}
                  className={`mt-1 ${errors.address_postalCode ? 'border-red-500' : ''}`}
                  placeholder="12345 or 12345-6789"
                  maxLength={10}
                />
                {errors.address_postalCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.address_postalCode}</p>
                )}
              </div>
            </div>
          </div>

          {/* Allergies Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2 flex items-center space-x-2">
              <span>Allergies</span>
              {allergiesLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            </h3>
            
            {allergiesError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">⚠️ {allergiesError}</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium text-gray-700">
                Add Medication Allergies
              </Label>
              <div className="mt-2">
                <AllergyInput
                  onAdd={handleAddAllergy}
                  placeholder="Search for medications..."
                  disabled={loading || allergiesLoading}
                />
              </div>
            </div>

            {allergiesLoading ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-vitl-green" />
                <p className="text-sm text-gray-500 mt-2">Loading current allergies...</p>
              </div>
            ) : selectedAllergies.length > 0 ? (
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Current Allergies
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedAllergies.map((medication) => (
                    <AllergyBadge
                      key={medication.id}
                      medication={medication.name}
                      onRemove={() => handleRemoveAllergy(medication.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">No allergies recorded</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-emerald-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white"
              disabled={loading || isSubmitting}
            >
              {loading || isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditPatientModal;